import { useEvents } from "../state";
import { Clock } from "./Clock";
import { useRef, useState, useEffect } from "react";

const dayNames = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const dayNamesShort = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"];

const monthNames = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

function getWeekDates(): Date[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date;
  });
}

function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface CalendarEvent {
  summary: string;
  start: string;
  end: string;
  isAllDay: boolean;
  color?: string;
}

interface RenderItem {
  type: "header" | "event";
  dayIndex: number;
  date?: Date;
  event?: CalendarEvent;
  showMonth?: boolean;
}

const ROW_HEIGHT = "h-10";
const ROW_HEIGHT_PX = 40; // h-10 = 2.5rem = 40px
const ROW_MARGIN_PX = 8;  // mb-2 = 0.5rem = 8px
const TOTAL_ROW_HEIGHT = ROW_HEIGHT_PX + ROW_MARGIN_PX; // 48px per item

function DayHeader({ date, showMonth }: { date: Date; showMonth?: boolean }) {
  return (
    <div
      className={`${ROW_HEIGHT} px-4 mb-2 flex items-center gap-2 text-black/70 ${isToday(date) ? "font-bold" : ""}`}
    >
      <span className="sm:hidden">{dayNamesShort[date.getDay()]}</span>
      <span className="hidden sm:inline">{dayNames[date.getDay()]}</span>
      <span>{date.getDate()}.</span>
      {showMonth && <span className="ml-1">{monthNames[date.getMonth()]}</span>}
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  return (
    <div className={`${ROW_HEIGHT} flex items-center px-4 mb-2`}>
      {!event.isAllDay && (
        <span
          className="text-black/50 min-w-43 px-2 py-1"
          style={{ backgroundColor: event.color || "#faedcb" }}
        >
          {formatTime(event.start)} - {formatTime(event.end)}
        </span>
      )}
      <div
        className={`grid grid-cols-1 flex-1 py-1 ${
          event.isAllDay ? "px-2" : "pr-2"
        }`}
        style={{ backgroundColor: event.color || "#faedcb" }}
      >
        <span className="truncate">{event.summary}</span>
      </div>
    </div>
  );
}

function RenderItems({ items }: { items: RenderItem[] }) {
  return (
    <>
      {items.map((item, i) =>
        item.type === "header" ? (
          <DayHeader
            key={`h-${item.dayIndex}`}
            date={item.date!}
            showMonth={item.showMonth}
          />
        ) : (
          <EventRow key={`e-${item.dayIndex}-${i}`} event={item.event!} />
        ),
      )}
    </>
  );
}

export function WeeklyPlan() {
  const weekDates = getWeekDates();
  const events = useEvents();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [splitIndex, setSplitIndex] = useState<number | null>(null);

  // Build flat list of render items
  const allItems: RenderItem[] = [];
  let lastMonth = -1;
  weekDates.forEach((date, dayIndex) => {
    const showMonth = date.getMonth() !== lastMonth;
    lastMonth = date.getMonth();
    allItems.push({ type: "header", dayIndex, date, showMonth });
    (events[dayIndex] || []).forEach((event) => {
      allItems.push({ type: "event", dayIndex, event });
    });
  });

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Calculate split point for desktop using fixed row heights
  useEffect(() => {
    if (isMobile) {
      setSplitIndex(null);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const calculateSplit = () => {
      const containerHeight = container.clientHeight;

      // Skip if container has no height yet
      if (containerHeight === 0) return;

      // Calculate how many items fit based on fixed row height
      const itemsThatFit = Math.floor(containerHeight / TOTAL_ROW_HEIGHT);

      // Ensure we don't split more than we have
      const split = Math.min(itemsThatFit, allItems.length);

      setSplitIndex(split);
    };

    // Use ResizeObserver to detect container size changes
    const observer = new ResizeObserver(() => {
      calculateSplit();
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [isMobile, allItems.length]);

  // Mobile: single scrollable column
  if (isMobile) {
    return (
      <div className="h-full py-4 overflow-y-auto">
        <div className="flex items-center gap-2 text-black/70 font-bold px-4 pb-2">
          <Clock />
        </div>
        <RenderItems items={allItems} />
      </div>
    );
  }

  // Desktop: two columns
  const firstColumn =
    splitIndex !== null ? allItems.slice(0, splitIndex) : allItems;
  const secondColumn = splitIndex !== null ? allItems.slice(splitIndex) : [];

  return (
    <div className="h-full py-4 overflow-hidden flex flex-col">
      <div className="flex items-center gap-2 text-black/70 font-bold px-4 pb-2">
        <Clock />
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-4 overflow-hidden">
        <div ref={containerRef} className="overflow-hidden">
          <RenderItems items={firstColumn} />
        </div>
        <div className="overflow-hidden">
          <RenderItems items={secondColumn} />
        </div>
      </div>
    </div>
  );
}
